/* ============================================================
   GLOBAL AI ASSISTANT HELPER & OPENAI KNOWLEDGE BASE ENGINE
   
   Features:
   1. ContextExtractor  – reads route + registered window data
   2. PromptBuilder     – generates dynamic context-aware prompts
   3. AIService         – produces natural language responses with:
      └─ Reference Document Knowledge Base (Entelipro AI ERP Vol 1-10)
      └─ Unanswered Query Logger & Admin Review Engine
      └─ Dynamic Knowledge Base Auto-Updater (Continuous Learning)
   ============================================================ */

import { getAuthHeader, isLoggedIn, isUserLoggedIn, removeToken, clearToken, notifyLogout } from "../../Utils/auth";
import { path_url } from "../../Config/config";

/* ------------------------------------------------------------
   1. REFERENCE DOCUMENT KNOWLEDGE BASE (Entelipro AI ERP)
   ------------------------------------------------------------ */
export const ENTELIPRO_KNOWLEDGE_BASE = [
  // Volume 1 – Introduction
  {
    volume: "Volume 1 – Introduction",
    chapter: "Chapter 1 – What is Entelipro AI ERP?",
    topics: ["what is entelipro", "why entelipro", "vision", "mission", "ai first erp", "cloud native erp", "multi tenant architecture", "microservice architecture", "enterprise ready", "ai agents", "digital workforce"],
    content: "Entelipro AI ERP is an enterprise-grade ERP system built to compete with SAP, Oracle NetSuite, Microsoft Dynamics 365, Zoho, Odoo, ERPNext, and Salesforce. It features an AI-First, Cloud Native, Multi-Tenant, Microservice Architecture designed for digital workforces, automated AI agents, and paperless operations."
  },
  {
    volume: "Volume 1 – Introduction",
    chapter: "Chapter 2 – Problems Solved",
    topics: ["problems solved", "excel dependency", "multiple software", "duplicate entries", "manual approvals", "lost customer data", "delayed reporting", "inventory mismatch", "poor employee tracking", "compliance issues", "no ai assistance", "no automation", "no centralized documents"],
    content: "Entelipro eliminates traditional business friction including Excel dependency, software fragmentation, duplicate data entries, slow manual approvals, lost customer records, delayed reporting, inventory mismatches, poor employee tracking, compliance failures, lack of AI assistance, and uncentralized documentation."
  },
  {
    volume: "Volume 1 – Introduction",
    chapter: "Chapter 3 – Core Principles",
    topics: ["core principles", "single source of truth", "ai driven decisions", "automation first", "security first", "cloud native", "mobile first", "api first", "paperless office", "digital transformation"],
    content: "Entelipro operates on core engineering principles: Single Source of Truth, AI-Driven Decisions, Automation First, Security First, Cloud Native, Mobile First, API First, Paperless Office, and end-to-end Digital Transformation."
  },

  // Volume 2 – Company Setup
  {
    volume: "Volume 2 – Company Setup",
    chapter: "Company Registration & Operations",
    topics: ["company setup", "company registration", "branch setup", "departments", "designations", "roles", "permissions", "business units", "cost centers", "financial years", "currencies", "languages", "timezones"],
    content: "Volume 2 covers Company Setup: Multi-company registration, branch hierarchy, department creation, designation mapping, role-based access control (RBAC), custom permissions, business units, cost centers, multi-currency support, localization languages, timezones, and financial year configuration."
  },
  {
    volume: "Volume 2 – Company Setup",
    chapter: "Compliance & Gateway Integrations",
    topics: ["gst", "pan", "tan", "iec", "msme", "startup registration", "digital signature", "email configuration", "sms configuration", "whatsapp configuration", "payment gateway", "bank accounts"],
    content: "Company compliance integrations in Entelipro support GST, PAN, TAN, Import Export Code (IEC), MSME registration, Startup India registration, Digital Signatures, SMTP email setups, SMS gateways, WhatsApp API configuration, Payment Gateways (Razorpay/Stripe), and bank account reconciliations."
  },

  // Volume 3 – AI Engine
  {
    volume: "Volume 3 – AI Engine",
    chapter: "AI Assistant & Natural Language Queries",
    topics: ["ai engine", "ai assistant", "natural language queries", "pending invoices", "underperforming employee", "predict sales", "top customers"],
    content: "The Entelipro AI Engine allows natural language querying. Users can ask queries like 'Show pending invoices', 'Which employee is underperforming?', 'Predict next month sales', or 'Show top customers' to get immediate real-time insights."
  },
  {
    volume: "Volume 3 – AI Engine",
    chapter: "AI Agent Types",
    topics: ["ai agent types", "sales ai", "hr ai", "finance ai", "inventory ai", "marketing ai", "support ai", "recruitment ai", "legal ai", "compliance ai", "ceo ai", "coo ai", "cfo ai", "cto ai", "operations ai"],
    content: "Entelipro includes 14 specialized AI Agents: Sales AI, HR AI, Finance AI, Inventory AI, Marketing AI, Support AI, Recruitment AI, Legal AI, Compliance AI, CEO AI, COO AI, CFO AI, CTO AI, and Operations AI."
  },
  {
    volume: "Volume 3 – AI Engine",
    chapter: "AI Capabilities",
    topics: ["predictive analytics", "recommendation engine", "demand forecasting", "cash flow prediction", "fraud detection", "expense analysis", "inventory forecasting", "auto email writing", "document summarization", "meeting minutes", "voice commands", "image recognition", "ocr", "speech recognition", "chat assistant", "auto report generation", "auto workflow suggestions", "root cause analysis", "ai knowledge base", "ai copilot"],
    content: "Core AI capabilities include Predictive Analytics, Recommendation Engine, Demand Forecasting, Cash Flow Prediction, Fraud Detection, Expense Analysis, Inventory Forecasting, Auto Email Drafting, Document Summarization, Automated Meeting Minutes, Voice Commands, Image Recognition, Vision OCR, Speech Recognition, Auto Report Generation, Auto Workflow Suggestions, Root Cause Analysis, AI Knowledge Base, and AI Copilot."
  },

  // Volume 4 – CRM
  {
    volume: "Volume 4 – CRM",
    chapter: "Lead & Customer Lifecycle",
    topics: ["crm", "lead management", "opportunity", "deal pipeline", "customer timeline", "sales activities", "meetings", "calls", "email tracking", "whatsapp", "customer visits", "proposal", "quotation", "contract", "renewals", "amc", "support", "customer satisfaction", "sales forecast", "revenue forecast", "lead scoring", "duplicate detection"],
    content: "Volume 4 CRM details Lead Management, Opportunity Pipelines, Customer Timelines, Sales Activity Tracking, Meetings/Calls logs, Email & WhatsApp tracking, Customer Visits, Proposals, Quotations, Contracts, Renewals, Annual Maintenance Contracts (AMC), Helpdesk Support, Customer Satisfaction (CSAT), Sales/Revenue Forecasting, AI Lead Scoring, and Duplicate Detection."
  },

  // Volume 5 – Sales
  {
    volume: "Volume 5 – Sales",
    chapter: "Sales & Billing Operations",
    topics: ["sales", "products", "price lists", "discounts", "coupons", "quotations", "sales orders", "invoices", "payments", "credit notes", "returns", "subscriptions", "recurring billing", "pos", "retail sales", "online orders", "marketplace integration", "commission", "sales targets", "collections"],
    content: "Volume 5 covers Sales: Product Catalogs, Price Lists, Discount Rules, Coupon Codes, Quotations, Sales Orders, Tax Invoicing, Payment Capture, Credit Notes, Returns Management, Subscriptions, Recurring Billing, Point of Sale (POS), Retail Sales, E-commerce Online Orders, Marketplace Integrations, Commission Management, Sales Targets, and Payment Collections."
  },

  // Volume 6 – Purchase
  {
    volume: "Volume 6 – Purchase",
    chapter: "Procurement & Vendor Management",
    topics: ["purchase", "procurement", "vendor management", "purchase request", "rfq", "purchase order", "goods receipt", "vendor invoice", "vendor payments", "vendor rating", "price comparison", "contract management"],
    content: "Volume 6 Purchase covers Vendor Relationship Management, Purchase Requests (PR), Request for Quotation (RFQ), Purchase Orders (PO), Goods Receipt Notes (GRN), Vendor Invoicing, Vendor Payment Schedules, Vendor Performance Rating, Supplier Price Comparison, and Vendor Contract Management."
  },

  // Volume 7 – Inventory
  {
    volume: "Volume 7 – Inventory",
    chapter: "Warehouse & Stock Control",
    topics: ["inventory", "warehouses", "bins", "batch", "serial number", "stock transfer", "cycle counting", "inventory adjustment", "minimum stock", "maximum stock", "reorder level", "expiry management", "barcode", "qr code", "rfid", "warehouse maps", "inventory ai"],
    content: "Volume 7 Inventory details Multi-warehouse management, Bin locations, Batch tracking, Serial Number tracking, Inter-warehouse Stock Transfer, Cycle Counting, Inventory Adjustments, Min/Max stock thresholds, Reorder Level alerts, Expiry Management, Barcode/QR Code/RFID scanning, 2D/3D Warehouse Maps, and Inventory AI optimization."
  },

  // Volume 8 – Manufacturing
  {
    volume: "Volume 8 – Manufacturing",
    chapter: "Production & Shop Floor Control",
    topics: ["manufacturing", "bill of materials", "bom", "production planning", "mrp", "machine management", "capacity planning", "production orders", "quality control", "waste management", "maintenance", "machine downtime", "iot integration"],
    content: "Volume 8 Manufacturing covers Bill of Materials (BOM), Production Planning & Scheduling, Material Requirements Planning (MRP), Machine Management, Capacity Planning, Work & Production Orders, Quality Control (QC), Waste/Scrap Management, Preventive Maintenance, Machine Downtime tracking, and Industrial IoT Integration."
  },

  // Volume 9 – HRMS
  {
    volume: "Volume 9 – HRMS",
    chapter: "Human Capital Management",
    topics: ["hrms", "employee profile", "attendance", "leave", "payroll", "loan", "advance", "performance", "kpi", "okr", "goals", "recruitment", "interview", "offer letter", "joining", "onboarding", "training", "skill matrix", "certificates", "travel", "expense", "assets", "exit process", "experience letter", "posh", "employee engagement", "recognition", "shift management", "work from home", "geo attendance", "face attendance", "biometric"],
    content: "Volume 9 HRMS details Employee Profiles, Geo/Face/Biometric Attendance, Leave Management, Automated Payroll, Employee Loans/Advances, Performance Reviews (KPI/OKR/Goals), Applicant Tracking (ATS), Interview Scheduling, Offer Letters, Onboarding, Skill Matrix, Asset Allocation, Exit Offboarding, POSH compliance, Shift Management, and WFH tracking."
  },

  // Volume 10 – Finance
  {
    volume: "Volume 10 – Finance",
    chapter: "Financial Accounting & General Ledger",
    topics: ["finance", "accounting", "chart of accounts", "general ledger", "journal entry", "bank reconciliation", "cash flow"],
    content: "Volume 10 Finance covers Chart of Accounts (COA), Multi-currency General Ledger (GL), Automated & Manual Journal Entries, Automated Bank Reconciliation, Cash Flow Statement generation, and Financial Reporting."
  }
];

/* ------------------------------------------------------------
   2. UNANSWERED QUERY STORAGE & KNOWLEDGE BASE STORAGE HELPERS
   ------------------------------------------------------------ */
const STORAGE_KEY_UNANSWERED = "entellipro_unanswered_queries_v1";
const STORAGE_KEY_CUSTOM_KB  = "entellipro_custom_kb_v1";

export const getUnansweredQueries = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_UNANSWERED);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error("Error reading unanswered queries:", e);
    return [];
  }
};

export const saveUnansweredQueries = (list) => {
  try {
    localStorage.setItem(STORAGE_KEY_UNANSWERED, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent("entellipro_unanswered_updated", { detail: list }));
  } catch (e) {
    console.error("Error saving unanswered queries:", e);
  }
};

export const getCustomKnowledgeBase = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_CUSTOM_KB);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error("Error reading custom KB:", e);
    return [];
  }
};

export const saveCustomKnowledgeBase = (list) => {
  try {
    localStorage.setItem(STORAGE_KEY_CUSTOM_KB, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent("entellipro_kb_updated", { detail: list }));
  } catch (e) {
    console.error("Error saving custom KB:", e);
  }
};

/**
 * Log an unanswered query (Rule 2, 3, 4)
 */
export const logUnansweredQuery = (question, pageModule, contextInfo = "") => {
  const list = getUnansweredQueries();
  
  // Avoid duplicate exact pending questions in the same module
  const existing = list.find(q => 
    q.question.toLowerCase().trim() === question.toLowerCase().trim() && 
    q.pageModule === pageModule &&
    q.status === "pending"
  );
  
  if (existing) return existing;

  const newRecord = {
    id: "unanswered-" + Date.now() + "-" + Math.floor(Math.random() * 1000),
    question: question.trim(),
    timestamp: new Date().toISOString(),
    displayTime: new Date().toLocaleString(),
    pageModule: pageModule || "General",
    context: typeof contextInfo === "string" ? contextInfo : JSON.stringify(contextInfo),
    status: "pending" // pending | resolved
  };

  const updated = [newRecord, ...list];
  saveUnansweredQueries(updated);
  return newRecord;
};

/**
 * Admin adds an answer to the knowledge base (Rule 5, 6, 7)
 */
export const addKnowledgeBaseEntry = (topic, volume, content, answerForQueryId = null) => {
  const customKB = getCustomKnowledgeBase();
  
  const newEntry = {
    id: "kb-custom-" + Date.now(),
    volume: volume || "Volume 11 – Extended Admin Knowledge Base",
    chapter: "Admin Custom Entry",
    topics: topic.toLowerCase().split(/[\s,]+/).filter(Boolean),
    title: topic,
    content: content,
    addedAt: new Date().toISOString()
  };

  const updatedKB = [newEntry, ...customKB];
  saveCustomKnowledgeBase(updatedKB);

  // If tied to a logged unanswered query, mark it resolved
  if (answerForQueryId) {
    const unanswered = getUnansweredQueries();
    const updatedUnanswered = unanswered.map(q => 
      q.id === answerForQueryId ? { ...q, status: "resolved", resolvedAnswer: content } : q
    );
    saveUnansweredQueries(updatedUnanswered);
  }

  return newEntry;
};

/**
 * Search Reference Document + Custom Knowledge Base (Rule 1 & Rule 6)
 */
export const searchKnowledgeBase = (query) => {
  const q = query.toLowerCase().trim();
  const qTokens = q.split(/\s+/).filter(t => t.length > 2);

  // 1. Search Custom Knowledge Base (Admin added entries - Rule 6)
  const customKB = getCustomKnowledgeBase();
  for (const entry of customKB) {
    const titleMatch = entry.title && q.includes(entry.title.toLowerCase());
    const topicMatch = entry.topics && entry.topics.some(t => q.includes(t));
    const tokenMatchCount = qTokens.filter(tok => (entry.content || "").toLowerCase().includes(tok)).length;

    if (titleMatch || topicMatch || (qTokens.length > 0 && tokenMatchCount / qTokens.length >= 0.6)) {
      return {
        found: true,
        source: "Admin Updated Knowledge Base",
        volume: entry.volume,
        chapter: entry.chapter || "Custom Topic",
        content: entry.content
      };
    }
  }

  // 2. Search Static Built-in Entelipro Reference Document (Rule 1)
  let bestMatch = null;
  let highestScore = 0;

  for (const item of ENTELIPRO_KNOWLEDGE_BASE) {
    let score = 0;
    
    // Direct topic match
    for (const topic of item.topics) {
      if (q.includes(topic)) {
        score += 10;
      }
    }

    // Token match in content
    let matchCount = 0;
    for (const tok of qTokens) {
      if (item.content.toLowerCase().includes(tok) || item.volume.toLowerCase().includes(tok)) {
        matchCount++;
      }
    }

    if (qTokens.length > 0) {
      score += (matchCount / qTokens.length) * 5;
    }

    if (score > highestScore) {
      highestScore = score;
      bestMatch = item;
    }
  }

  if (bestMatch && highestScore >= 5) {
    return {
      found: true,
      source: "Entelipro AI ERP Reference Document",
      volume: bestMatch.volume,
      chapter: bestMatch.chapter,
      content: bestMatch.content
    };
  }

  return { found: false };
};

/* ------------------------------------------------------------
   3. CONTEXT EXTRACTOR
   ------------------------------------------------------------ */
export const ContextExtractor = {
  getPageContext(pathname, windowCtx) {
    const path = (pathname || "").toLowerCase();

    if (path.includes("vendor-details")) {
      return {
        type: "vendor",
        label: "Vendor Details",
        data: windowCtx?.type === "vendor" ? windowCtx : { type: "vendor", profile: {}, details: {} }
      };
    }
    if (path.includes("customer-details")) {
      return {
        type: "customer",
        label: "Customer Details",
        data: windowCtx?.type === "customer" ? windowCtx : { type: "customer", profile: {}, details: {} }
      };
    }
    if (path.includes("lead-details")) {
      return {
        type: "lead",
        label: "Lead Details",
        data: windowCtx?.type === "lead" ? windowCtx : { type: "lead", profile: {}, details: {} }
      };
    }
    if (path.includes("vendor-dashboard"))   return { type: "vendor_dashboard",   label: "Vendor Dashboard",   data: {} };
    if (path.includes("customer-dashboard")) return { type: "customer_dashboard", label: "Customer Dashboard", data: {} };
    if (path.includes("lead-dashboard"))     return { type: "lead_dashboard",     label: "Lead Dashboard",     data: {} };
    if (path.includes("task-dashboard"))     return { type: "task_dashboard",     label: "Task Dashboard",     data: {} };
    if (path.includes("crm-dashboard"))      return { type: "dashboard",          label: "CRM Dashboard",      data: {} };
    if (path.includes("task-details"))       return { type: "task",               label: "Task Details",       data: {} };

    if (path.includes("purchase") || path.includes("procurement") || path.includes("order")) {
      return { type: "purchase", label: "Purchase Module", data: {} };
    }
    if (path.includes("inventory") || path.includes("warehouse")) {
      return { type: "inventory", label: "Inventory Module", data: {} };
    }
    if (path.includes("account") || path.includes("finance") || path.includes("invoice") || path.includes("payroll")) {
      return { type: "accounting", label: "Finance & Accounting Module", data: {} };
    }
    if (path.includes("leads/")) return { type: "leads_list", label: "Leads List", data: {} };
    if (path.includes("customers/")) return { type: "customers_list", label: "Customers List", data: {} };
    if (path.includes("vendors/")) return { type: "vendors_list", label: "Vendors List", data: {} };

    // --- Public Pages ---
    if (path === "/" || path === "/home") {
      return { type: "public_home", label: "Home Page", data: {} };
    }
    if (path.includes("about") || path.includes("ourstory") || path.includes("whychooseus")) {
      return { type: "public_about", label: "About Us", data: {} };
    }
    if (path.includes("eyesense")) {
      return { type: "public_eyesense", label: "EyeSense AI", data: {} };
    }
    if (path.includes("services") || path.includes("customer_services")) {
      return { type: "public_services", label: "Services Page", data: {} };
    }
    if (path.includes("resources")) {
      return { type: "public_resources", label: "Resources Hub", data: {} };
    }
    if (path.includes("features")) {
      return { type: "public_features", label: "Core Features", data: {} };
    }
    if (path.includes("pricing")) {
      return { type: "public_pricing", label: "Pricing Tiers", data: {} };
    }
    if (path.includes("contact_us") || path.includes("contactmanagement")) {
      return { type: "public_contact", label: "Contact Us", data: {} };
    }
    if (
      path.includes("aerospace") || path.includes("transport") || path.includes("computer") ||
      path.includes("telecommunication") || path.includes("pharmaceutical") || path.includes("food") ||
      path.includes("healthcare") || path.includes("construction") || path.includes("agriculture") ||
      path.includes("education") || path.includes("electronics")
    ) {
      return { type: "public_industry", label: "Industry Solutions", data: { industry: path.replace("/", "") } };
    }

    return { type: "general", label: "CRM Application", data: { path: pathname } };
  }
};

/* ------------------------------------------------------------
   4. PROMPT BUILDER
   ------------------------------------------------------------ */
export const PromptBuilder = {
  buildSystemPrompt(contextType, data) {
    const entity = this._entityName(contextType, data);
    switch (contextType) {
      case "vendor":
        return `You are a CRM Vendor Relationship Advisor. The user is viewing the vendor profile of "${entity}". Base answers on the reference document.`;
      case "customer":
        return `You are a CRM Customer Success Advisor. The user is viewing the customer profile of "${entity}". Base answers on the reference document.`;
      case "lead":
        return `You are a CRM Sales Advisor. The user is viewing the lead profile of "${entity}". Base answers on the reference document.`;
      default:
        return `You are a helpful AI assistant. Always consult the reference document first. Log unanswered queries for admin updates.`;
    }
  },
  _entityName(type, data) {
    const profile = data?.profile || {};
    if (profile.firstname || profile.lastName) {
      return `${profile.firstname || ""} ${profile.lastName || profile.lastname || ""}`.trim();
    }
    return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

/* ============================================================
   ENTELIPRO QUESTION BANK
   Layer 1 — Local answers. If matched, return instantly.
   No API call needed.
   ============================================================ */
export const QuestionBank = [

  // ── About EnteliPro ─────────────────────────────────────
  {
    keywords: ["what is entelipro", "tell me about entelipro", "entelipro kya hai", "about entelipro", "explain entelipro"],
    answer: `**EnteliPro** is a complete Enterprise CRM and ERP platform built for modern businesses.\n\nIt combines Sales, Marketing, Purchase, Inventory, Accounting, and Customer Support into one unified system — so your entire team works from a single source of truth.\n\nBuilt-in AI (EnteliPro AI Assistant) helps users make smarter decisions directly inside the platform.`
  },
  {
    keywords: ["who made entelipro", "who built entelipro", "company behind entelipro", "indsac", "entelipro company"],
    answer: `**EnteliPro** is a product by **Indsac**.\n\nIndsac's mission is to eliminate fragmented business workflows by providing companies with one integrated platform that connects their CRM, ERP, and AI capabilities together.`
  },
  {
    keywords: ["what does entelipro do", "entelipro kya karta hai", "purpose of entelipro", "why entelipro"],
    answer: `EnteliPro helps businesses:\n\n**1.** Manage customers, leads, and vendors in one place\n**2.** Track sales, purchases, and inventory automatically\n**3.** Handle accounting, invoices, and payments\n**4.** Get AI-powered insights and recommendations in real time\n\nInstead of using 5 different apps, everything works together in EnteliPro.`
  },

  // ── Modules ─────────────────────────────────────────────
  {
    keywords: ["what modules", "which modules", "features list", "modules in entelipro", "kya kya hai entelipro mein", "modules available"],
    answer: `EnteliPro includes the following modules:\n\n**CRM:** Leads, Customers, Vendors, Interactions, Tasks\n**Operations:** Sales, Purchase, Inventory\n**Finance:** Accounting, Invoices, Payments\n**Communication:** Email, Bulk Email, Service Requests\n**Marketing:** Campaigns, Social Media (Enteli Social)\n**AI:** EnteliPro AI Assistant on every page\n**Settings:** Users, Roles, Security, Permissions`
  },
  {
    keywords: ["lead module", "lead management", "lead kya hai", "what is lead", "leads kaise manage"],
    answer: `The **Lead Module** in EnteliPro manages potential customers who have shown interest in your business.\n\n**Key features:**\n- Lead funnel with stages (New → Contacted → Qualified → Proposal → Won/Lost)\n- Lead activity log (calls, emails, meetings, notes)\n- Stage history — automatic tracking of every status change\n- Lead dashboard with KPIs and conversion analytics\n- Lead scoring to prioritize hot prospects`
  },
  {
    keywords: ["customer module", "customer management", "customer kya hai", "what is customer in erp"],
    answer: `The **Customer Module** stores and manages all your existing buyers.\n\n**What it tracks:**\n- Contact details, company info, billing address\n- Full purchase and invoice history\n- Interaction timeline (every call, email, meeting)\n- Open tasks and service requests\n- AI-powered health analysis and churn risk alerts`
  },
  {
    keywords: ["vendor module", "vendor management", "vendor kya hai", "what is vendor", "supplier"],
    answer: `The **Vendor Module** manages all companies and individuals you buy from.\n\n**Key features:**\n- Vendor profile with contact, bank details, payment terms\n- Purchase order history\n- Invoice and payment tracking\n- Interaction records — every communication logged\n- AI Vendor Advisor gives negotiation tips and risk alerts`
  },
  {
    keywords: ["inventory module", "inventory kya hai", "stock management", "warehouse", "inventory dashboard"],
    answer: `The **Inventory Module** tracks everything your business owns in stock.\n\n**It shows you:**\n- Total stock value and turnover rate\n- Out-of-stock and low-stock alerts\n- Fast-moving vs slow-moving items\n- Stock movement (incoming vs outgoing)\n- Multi-warehouse breakdown\n- Dead stock and expiry alerts`
  },
  {
    keywords: ["sales module", "sales kya hai", "quotation", "sales order", "sales process"],
    answer: `The **Sales Module** manages your entire revenue process:\n\n**Flow:** Lead → Quotation → Sales Order → Delivery → Invoice → Payment\n\n**Features:**\n- Create and send quotations\n- Convert to sales orders automatically\n- Track delivery status\n- Raise invoices and record payments\n- Sales dashboard with pipeline and rep performance`
  },
  {
    keywords: ["purchase module", "purchase kya hai", "procurement", "purchase order", "vendor bill", "buying process"],
    answer: `The **Purchase Module** manages everything you buy from vendors:\n\n**Flow:** Purchase Request → RFQ → Purchase Order → Goods Receipt → Vendor Bill → Payment\n\n**Features:**\n- Request for Quotation to multiple vendors\n- Purchase order creation and approval\n- Goods receipt to update inventory automatically\n- Vendor bill matching and payment tracking`
  },
  {
    keywords: ["accounting module", "finance module", "accounting kya hai", "ledger", "balance sheet", "profit loss"],
    answer: `The **Accounting Module** records every financial transaction in EnteliPro.\n\n**Key features:**\n- Accounts Receivable — money customers owe you\n- Accounts Payable — money you owe vendors\n- Journal entries and general ledger\n- Tax management (GST, VAT, TDS)\n- Financial reports: P&L, Balance Sheet, Cash Flow\n- Bank reconciliation`
  },
  {
    keywords: ["task module", "task management", "task kya hai", "tasks", "to do", "todo"],
    answer: `The **Task Module** tracks all internal work across your team.\n\n**Features:**\n- Create tasks with due date, priority, and assignee\n- Link tasks to any Customer, Lead, or Vendor record\n- Subtasks and checklists\n- Comments on tasks\n- Kanban board view (To Do / In Progress / Done)\n- Overdue alerts and follow-up reminders`
  },
  {
    keywords: ["service request", "helpdesk", "support ticket", "ticket", "customer complaint", "service module"],
    answer: `The **Service Request Module** handles all customer support issues.\n\n**Flow:** Issue raised → Ticket created → Assigned to agent → Resolved → Closed\n\n**Features:**\n- Priority levels (Low / Medium / High / Urgent)\n- SLA tracking — auto-escalate if not resolved in time\n- Internal notes + customer-visible replies\n- Linked to Customer and Sales Order\n- CSAT survey after resolution`
  },
  {
    keywords: ["interaction module", "interaction kya hai", "what is interaction", "communication log", "activity log"],
    answer: `The **Interaction Module** logs every communication with a customer, lead, or vendor.\n\n**Types of interactions:**\n- Phone call, Email, Meeting, WhatsApp/SMS, Internal Note\n\n**Why it matters:**\nIf a sales rep leaves, all conversations are saved. Any team member can open a record and see the full history — who said what and when.\n\nEvery interaction can also set a follow-up reminder so nothing falls through the cracks.`
  },
  {
    keywords: ["enteli social", "social media", "social media posting", "post on social media", "instagram facebook linkedin"],
    answer: `**Enteli Social** is EnteliPro's built-in social media management module.\n\n**How it works:**\n1. Go to Settings → connect your social accounts (Instagram, Facebook, LinkedIn, etc.)\n2. Go to Enteli Social module\n3. Create your post (text, image, video)\n4. Select which platforms to post on\n5. Publish to all selected platforms from one place\n\nNo need to open each app separately — one post, multiple platforms.`
  },
  {
    keywords: ["email module", "send email", "bulk email", "email kaise bhejein", "email campaign"],
    answer: `EnteliPro has three email features:\n\n**1. Email Module** — Your inbox synced inside the ERP. Every email auto-linked to the relevant customer or vendor.\n\n**2. Send Email** — One-to-one email to a specific customer, lead, or vendor directly from their record.\n\n**3. Bulk Email** — Send mass emails to a segmented list. Track opens, clicks, and unsubscribes. Used for campaigns and announcements.`
  },

  // ── AI / EnteliPro AI Assistant ────────────────────────────
  {
    keywords: ["what is zia", "who is zia", "zia kya hai", "zia kya karta hai", "ai assistant", "chatbot", "entelipro ai assistant"],
    answer: `**EnteliPro AI Assistant** is EnteliPro's built-in AI assistant — available on every page of the platform.\n\n**What it can do:**\n- Summarize any customer, lead, or vendor record\n- Identify risks and issues automatically\n- Suggest next actions based on current data\n- Draft follow-up emails\n- Give negotiation tips for vendors\n- Answer any question about what's on the current page\n\nEnteliPro AI Assistant reads the page context automatically — so answers are always specific, never generic.`
  },
  {
    keywords: ["how does ai work", "ai kaise kaam karta hai", "how does zia work", "zia kaise kaam karta hai"],
    answer: `EnteliPro AI Assistant works in two steps:\n\n**Step 1 — Context building:** When you ask a question, the assistant automatically reads which page you're on and what record you're viewing (which customer, vendor, or lead).\n\n**Step 2 — AI processing:** Your question + the page context is sent to the AI model. The AI understands both your question and the current data, then gives a specific, actionable answer.\n\nThis is why it says "Sunita Sharma has 3 pending invoices" instead of a generic response — it reads the actual data.`
  },
  {
    keywords: ["eyesense", "eyesense ai", "document ai", "ocr", "invoice scanning", "receipt scanning"],
    answer: `**EyeSense AI** is EnteliPro's Vision and Document AI engine.\n\n**What it does:**\n- Automatically reads and extracts data from scanned invoices\n- Processes handwritten forms and receipts\n- Parses PDF documents and converts them to structured data\n- Reduces manual data entry to near zero\n\nEyeSense uses dual-pass verification to achieve 99.4% accuracy on document recognition.`
  },

  // ── Pricing & Plans ──────────────────────────────────────
  {
    keywords: ["pricing", "plans", "how much", "kitna cost", "subscription", "price", "plan kya hai"],
    answer: `EnteliPro offers three plans:\n\n**Starter** — Perfect for small teams. Core CRM and basic operations.\n\n**Professional** — Full CRM + ERP automation. Sales, Purchase, Inventory, Accounting, AI included.\n\n**Enterprise** — Dedicated database, custom SLAs, custom integrations, priority support.\n\nAnnual billing saves 20% across all plans. Contact us for exact pricing based on your team size.`
  },
  {
    keywords: ["free trial", "try entelipro", "demo", "free plan", "trial"],
    answer: `Yes! EnteliPro offers a **free 14-day trial** — no credit card required.\n\nYou get access to all modules during the trial so you can explore everything before deciding.\n\nGo to the Register page to start your trial instantly.`
  },

  // ── Industries ───────────────────────────────────────────
  {
    keywords: ["which industries", "industry support", "kon si industry", "sectors", "what industries"],
    answer: `EnteliPro serves businesses across these industries:\n\n**Manufacturing & Engineering:** Aerospace, Construction, Electronics\n**Healthcare & Life Sciences:** Pharmaceuticals, Healthcare\n**Technology:** Computer Hardware, Telecommunications\n**Consumer:** Food & Beverage, Agriculture\n**Services:** Education, Logistics & Transportation\n\nEach industry gets custom-configured modules, schemas, and compliance frameworks tailored to their specific needs.`
  },

  // ── Getting Started ──────────────────────────────────────
  {
    keywords: ["how to start", "kaise shuru karein", "getting started", "onboarding", "how to use"],
    answer: `Getting started with EnteliPro is simple:\n\n**1.** Sign up for a free 14-day trial at entelipro.com\n**2.** Set up your company profile in Settings\n**3.** Add your team members and assign roles\n**4.** Import your existing customers, leads, and vendors (CSV upload available)\n**5.** Configure your tax rates, invoice numbering, and payment terms\n**6.** Start using the modules — Sales, Purchase, Inventory, CRM\n\nOur team is available to guide you through implementation if needed.`
  },
  {
    keywords: ["contact", "contact us", "reach out", "customer support", "helpline"],
    answer: `You can reach the EnteliPro team through:\n\n**Email:** support@entelipro.com\n**Contact Form:** Available on the Contact Us page\n**In-app Support:** Logged-in users can submit a support ticket directly from the dashboard\n\nTypical response time is under 4 hours for standard queries.`
  },

  // ── Security & Settings ──────────────────────────────────
  {
    keywords: ["security", "roles", "permissions", "access control", "rbac", "user management", "who can access"],
    answer: `EnteliPro has a full **Role-Based Access Control (RBAC)** system.\n\n**Roles available:**\n- Admin — full access to everything\n- Manager — team visibility, approvals\n- Sales Team — leads, customers, quotations\n- Purchase Team — vendors, POs, bills\n- Support Team — service requests, tickets\n- Employee — tasks and assigned records only\n\nAdmins can create custom roles and define exactly which modules and actions each role can access.`
  },
  {
    keywords: ["settings", "configuration", "company setup", "settings module", "configure"],
    answer: `The **Settings Module** is the admin control center of EnteliPro.\n\n**What you configure here:**\n- Company name, logo, address, tax registration\n- User accounts and role assignments\n- Invoice and PO numbering formats\n- Tax rates (GST, VAT, TDS)\n- Email/SMTP configuration\n- Social media platform connections (for Enteli Social)\n- Two-factor authentication\n- Audit logs — who changed what and when`
  },

  // ── General ERP / CRM questions ──────────────────────────
  {
    keywords: ["what is erp", "erp kya hai", "erp meaning", "enterprise resource planning"],
    answer: `**ERP** stands for Enterprise Resource Planning.\n\nIt is a software system that connects all parts of a business — sales, purchasing, inventory, finance, HR — into one single platform with a shared database.\n\nInstead of using separate apps for each department that don't talk to each other, an ERP like EnteliPro connects everything. One sale automatically updates inventory, raises an invoice, and posts to accounting — without anyone doing it manually.`
  },
  {
    keywords: ["what is crm", "crm kya hai", "crm meaning", "customer relationship management"],
    answer: `**CRM** stands for Customer Relationship Management.\n\nIt is a system that helps businesses manage all their interactions with customers and potential customers (leads) in one place.\n\nIn EnteliPro, the CRM covers:\n- Lead tracking and conversion\n- Customer profiles with full history\n- Vendor relationship management\n- Interaction logs (calls, emails, meetings)\n- Tasks and follow-up reminders`
  }
];

/* ============================================================
   LAYER 1 — LOCAL MATCHER
   Try to match user question against QuestionBank.
   Returns answer string if found, null if not found.
   ============================================================ */
export function findLocalAnswer(question) {
  const q = (question || "").toLowerCase().trim();
  for (const entry of QuestionBank) {
    const matched = entry.keywords.some(keyword =>
      q.includes(keyword.toLowerCase())
    );
    if (matched) return entry.answer;
  }
  return null; // no match — fall through to Python AI
}

/* ------------------------------------------------------------
   5. AI SERVICE WITH REFERENCE KB + UNANSWERED LOGGING RULES
   ------------------------------------------------------------ */

// NOTE: Token reading is handled by src/utils/auth.js — do not read localStorage here directly.

export const AIService = {
  getUnansweredQueries,
  saveUnansweredQueries,
  getCustomKnowledgeBase,
  addKnowledgeBaseEntry,
  logUnansweredQuery,

  /**
   * Send a question to the Spring Boot → Python AI backend.
   *
   * @param {string}      question    - Raw user question text
   * @param {string}      contextType - Page context type
   * @param {object}      data        - Page context data
   * @param {Array}       history     - Previous messages (kept for compat)
   * @param {object|null} guestUser   - { name, email, mobile } for logged-out users
   * @returns {Promise<string>}       - AI response text
   */
  async ask(question, contextType, data, history = [], guestUser = null) {

    // ── LAYER 1: Try local QuestionBank first (instant, no API call) ──
    const localAnswer = findLocalAnswer(question);
    if (localAnswer) {
      return localAnswer;
    }

    // ── LAYER 2: Call Spring Boot → Python AI backend ──
    const systemPrompt = PromptBuilder.buildSystemPrompt(contextType, data);

    const guestPrefix = guestUser
      ? `Visitor: Name: ${guestUser.name}, Email: ${guestUser.email}, Mobile: ${guestUser.mobile}. `
      : "";

    const enrichedQuery = `${systemPrompt}\n\n${guestPrefix}User question: ${question}`;

    // Get auth header from shared utility
    const authHeader = getAuthHeader();

    const headers = {
      "Content-Type": "application/json",
      ...authHeader,
    };

    const BASE_URL = process.env.REACT_APP_API_BASE_URL || path_url || "http://localhost:8080";
    const primaryUrl = `${BASE_URL.replace(/\/$/, "")}/api/ai/python/ask`;
    const fallbackUrl = "/api/ai/python/ask";

    console.log("=== ZIA AI DEBUG ===");
    console.log("Logged in:", isUserLoggedIn ? isUserLoggedIn() : isLoggedIn());
    console.log("Auth header:", authHeader);
    console.log("Calling Primary Endpoint:", primaryUrl);
    console.log("===================");

    let response;
    try {
      // First attempt: call Spring Boot port 8080 directly (matches all other API calls in app)
      response = await fetch(primaryUrl, {
        method: "POST",
        headers,
        body: JSON.stringify({ query: enrichedQuery }),
      });

      // If primary returns 403 or 404, attempt fallback relative path
      if ((response.status === 403 || response.status === 404) && primaryUrl !== fallbackUrl) {
        console.warn(`[EnteliPro AI] Primary endpoint returned ${response.status}. Trying fallback proxy route: ${fallbackUrl}`);
        const fallbackRes = await fetch(fallbackUrl, {
          method: "POST",
          headers,
          body: JSON.stringify({ query: enrichedQuery }),
        });
        if (fallbackRes.ok || fallbackRes.status !== 403) {
          response = fallbackRes;
        }
      }
    } catch (networkErr) {
      console.warn("[EnteliPro AI] Direct fetch failed, trying proxy fallback:", networkErr);
      try {
        response = await fetch(fallbackUrl, {
          method: "POST",
          headers,
          body: JSON.stringify({ query: enrichedQuery }),
        });
      } catch (fallbackErr) {
        console.error("[EnteliPro AI] Both primary and fallback requests failed:", fallbackErr);
        throw new Error(
          "Cannot reach server. Is Spring Boot running on port 8080?"
        );
      }
    }

    // Handle all error status cases
    if (response.status === 403) {
      console.error("[EnteliPro AI] 403 Forbidden — Check token & Spring Boot Security config");
      throw new Error(
        "Access denied (403). Token may be missing or invalid. Check console for auth header."
      );
    }
    if (response.status === 401) {
      if (typeof clearToken === "function") clearToken();
      if (typeof notifyLogout === "function") notifyLogout();
      throw new Error("Session expired (401). Please log in again.");
    }
    if (response.status === 404) {
      throw new Error(
        "Endpoint not found (404). Check Spring Boot is running and endpoint exists."
      );
    }
    if (!response.ok) {
      let errorDetail = "";
      try { errorDetail = await response.text(); } catch (_) {}
      console.error("[EnteliPro AI] HTTP", response.status, errorDetail);
      throw new Error(`Server error ${response.status}. Please try again.`);
    }

    let responseData;
    try {
      responseData = await response.json();
    } catch (parseErr) {
      console.error("[EnteliPro AI] JSON parse error:", parseErr);
      throw new Error("Received an unexpected response from the AI service.");
    }

    // Spring Boot returns { response: "...", cached: true/false }
    const result = responseData.response || responseData.answer || responseData.message || responseData.text;
    if (!result) {
      throw new Error("The AI returned an empty response. Please try rephrasing.");
    }
    return result;
  }
};

/* ------------------------------------------------------------
   PRIVATE GENERIC GUIDANCE & FALLBACK HELPERS
   ------------------------------------------------------------ */
function _profileName(profile) {
  if (!profile) return null;
  const first = profile.firstname || profile.firstName || profile.name || "";
  const last = profile.lastname || profile.lastName || "";
  return (first + " " + last).trim() || null;
}

function _contextLabel(type) {
  const labels = {
    vendor: "Vendor Details", customer: "Customer Details", lead: "Lead Details",
    dashboard: "CRM Dashboard", purchase: "Purchase Module",
    inventory: "Inventory Module", accounting: "Finance & Accounting",
    vendor_dashboard: "Vendor Dashboard", customer_dashboard: "Customer Dashboard",
    lead_dashboard: "Lead Dashboard", task_dashboard: "Task Dashboard",
    public_home: "Entelli Pro Home Page",
    public_about: "About Us Page",
    public_eyesense: "EyeSense AI",
    public_services: "Services Page",
    public_resources: "Resources Hub",
    public_features: "Core Features",
    public_pricing: "Pricing Tiers",
    public_contact: "Contact Us Page",
    public_industry: "Industry Solutions",
    general: "CRM Application Page"
  };
  return labels[type] || "Current Module";
}

function _provideGenericGuidance(type, name, profile, details, question) {
  switch (type) {
    case "vendor":
      return `For vendor management (**${name}**):\n- Verify order logs and invoice status under the vendor profile.\n- Maintain direct email or phone logs to avoid delivery bottlenecks.\n- Ensure contract renewal terms are reviewed annually.`;
    case "customer":
      return `For customer success (**${name}**):\n- Review active subscriptions and invoice payment history.\n- Conduct regular quarterly check-ins to monitor account satisfaction.\n- Log key decisions directly into the timeline notes.`;
    case "lead":
      return `For sales leads (**${name}**):\n- Ensure initial outreach occurs within 24 hours of lead creation.\n- Qualify customer budget, authority, need, and timeline (BANT).\n- Schedule follow-up tasks to advance the deal stage.`;
    case "purchase":
      return `For purchase operations:\n- Cross-reference Purchase Orders (PO) with Goods Receipt Notes (GRN).\n- Compare supplier prices before finalizing RFQs.\n- Track vendor performance ratings for delivery accuracy.`;
    case "inventory":
      return `For inventory management:\n- Audit warehouse bin allocations and stock levels regularly.\n- Set automated Reorder Level alerts to prevent stockouts.\n- Use barcode/QR scanning to reduce manual counting errors.`;
    case "accounting":
      return `For financial & accounting tasks:\n- Ensure general ledger journal entries are reconciled monthly.\n- Review overdue accounts receivable and issue payment reminders.\n- Monitor cash flow forecasts to maintain liquidity.`;
    default:
      return `While this specific detail is not in the knowledge base:\n- Check the active tab and form fields on this page.\n- Review system notifications or recent logs.\n- Contact your system administrator if assistance is required.`;
  }
}
